/* =========================================================================
   RYZO MOTION · GSAP + ScrollTrigger layer for the snap-deck homepage.

   Native CSS scroll-snap stays in charge of scrolling (a smooth-scroll
   library like Lenis would fight `scroll-snap-type: y mandatory`).
   GSAP only *reads* scroll position to drive:
     · a top progress bar
     · deck navigation dots (with feature labels)
     · parallax drift on each screenshot slide
     · the hero scroll hint
     · the final CTA slide entrance

   Everything degrades gracefully: without GSAP (or with reduced motion)
   the inline IntersectionObserver in theme.liquid still reveals slides.
   ========================================================================= */
(function () {
  'use strict';

  function init() {
    var root = document.documentElement;
    if (!root.classList.contains('ryzo-snap-home')) return;
    if (!window.gsap || !window.ScrollTrigger) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    window.gsap.registerPlugin(window.ScrollTrigger);
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    var slides = Array.prototype.slice.call(document.querySelectorAll('.ryzo-snap-page'));
    if (!slides.length) return;

    /* ----- Top progress bar ----- */
    var progress = document.createElement('div');
    progress.className = 'ryzo-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.appendChild(progress);
    gsap.to(progress, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: document.body,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.3
      }
    });

    /* ----- Deck navigation dots ----- */
    var nav = document.createElement('nav');
    nav.className = 'ryzo-deck-nav';
    nav.setAttribute('aria-label', 'Ryzo feature tour');

    slides.forEach(function (slide, i) {
      var label = slide.getAttribute('data-ryzo-label');
      if (!label) {
        if (slide.classList.contains('ryzo-hero')) label = 'Overview';
        else if (slide.classList.contains('ryzo-cta')) label = 'Get Ryzo';
        else label = 'Slide ' + (i + 1);
      }

      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ryzo-deck-nav__dot';
      dot.setAttribute('aria-label', label);

      var tip = document.createElement('span');
      tip.className = 'ryzo-deck-nav__label';
      tip.textContent = label;
      dot.appendChild(tip);

      dot.addEventListener('click', function () {
        slide.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      nav.appendChild(dot);

      ScrollTrigger.create({
        trigger: slide,
        start: 'top center',
        end: 'bottom center',
        onToggle: function (self) {
          if (!self.isActive) return;
          var current = nav.querySelector('.is-active');
          if (current) current.classList.remove('is-active');
          dot.classList.add('is-active');
        }
      });
    });
    document.body.appendChild(nav);

    /* ----- Hero: bold scroll affordance -----
       1. Cue hides once the reader starts the story.
       2. Idle "nudge": the hero content dips every few seconds — the page
          itself asks to be pulled down. Killed on first scroll.
       3. Exit scrub: hero content lifts and fades as you scroll away. */
    var cue = document.querySelector('.ryzo-hero__scroll-cue');
    if (cue && slides[1]) {
      /* Hide only once the first chapter is genuinely arriving — never on
         load-time layout shifts or tiny snap adjustments. */
      ScrollTrigger.create({
        trigger: slides[1],
        start: 'top 80%',
        onEnter: function () { cue.classList.add('is-hidden'); },
        onLeaveBack: function () { cue.classList.remove('is-hidden'); }
      });
    }

    var hero = document.querySelector('.ryzo-hero');
    var story = document.querySelector('.ryzo-hero__story');
    if (hero && story) {
      gsap.to(story, {
        yPercent: -10,
        opacity: 0.2,
        scale: 0.96,
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });
    }

    /* ----- Auto scroll hint: ~2s after load the page itself dips down a
       little and settles back — weighted in, soft out. Runs a few times at
       most, only while the reader is still at the very top, and is
       permanently cancelled by any real interaction. */
    (function () {
      var nudgeCount = 0;
      var cancelled = false;
      var nudging = false;
      var activeTl = null;
      var htmlStyle = document.documentElement.style;

      /* Cancel only on real scroll intent — a click (cookie banner, CTA
         hover-click, etc.) must NOT kill the hint. A nudge already in
         flight dies instantly: it must never wrestle the reader. */
      var cancel = function () {
        cancelled = true;
        if (activeTl) {
          activeTl.kill();
          activeTl = null;
          htmlStyle.scrollSnapType = '';
          htmlStyle.scrollBehavior = '';
          nudging = false;
        }
      };
      window.addEventListener('wheel', cancel, { passive: true });
      window.addEventListener('touchstart', cancel, { passive: true });
      window.addEventListener('keydown', function (e) {
        if ([' ', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].indexOf(e.key) !== -1) cancel();
      });
      /* Any scroll we didn't cause (deck dot click, scroll-cue click,
         anchor jump) also cancels — the nudge must never yank a reader
         back from where they chose to go. */
      window.addEventListener('scroll', function () {
        if (!nudging && window.scrollY > window.innerHeight * 0.6) cancel();
      }, { passive: true });

      /* behavior MUST be 'instant': the theme sets `scroll-behavior:
         smooth` on <html>, and 'auto' defers to it — every frame's update
         would start a new smooth animation and the dip never renders. */
      var apply = function (pos) {
        return function () { window.scrollTo({ top: pos.y, behavior: 'instant' }); };
      };

      var schedule = function () {
        if (cancelled || nudgeCount >= 3) return;
        setTimeout(doNudge, nudgeCount === 0 ? 2200 : 7000);
      };

      var doNudge = function () {
        /* The sticky header sits above the hero, so the hero's snap rest
           is ~80px — NOT 0. "Still on the hero" is the real condition. */
        if (cancelled || window.scrollY > window.innerHeight * 0.5) return;
        nudgeCount++;
        nudging = true;
        /* Suspend snap + CSS smooth scrolling for the performance —
           mandatory snap yanks the position back, smooth eats the frames. */
        htmlStyle.scrollSnapType = 'none';
        htmlStyle.scrollBehavior = 'auto';
        /* Peek: reveal the top slice of the second page, hold a beat,
           then settle back home — all weight, no bounce. */
        var home = window.scrollY;
        var peek = home + Math.round(window.innerHeight * 0.26);
        var pos = { y: home };
        activeTl = gsap.timeline({
          onComplete: function () {
            activeTl = null;
            htmlStyle.scrollSnapType = '';
            htmlStyle.scrollBehavior = '';
            nudging = false;
            schedule();
          }
        })
          .to(pos, { y: peek, duration: 0.95, ease: 'power2.inOut', onUpdate: apply(pos) })
          .to(pos, { y: home, duration: 1.25, ease: 'power3.inOut', delay: 0.35, onUpdate: apply(pos) });
      };

      schedule();
    })();

    /* ----- Companion orb: a 3D morphing blob that evolves through the
       story. One scrubbed timeline across the full page; each slide gets
       a "stop". Between stops the blob morphs to its next shape (MorphSVG)
       and does a full 3D Y-spin, so it visibly *evolves* page by page,
       finally melting into a glow behind the CTA. */
    if (window.matchMedia('(min-width: 900px)').matches && slides.length > 2) {
      var BLOBS = [
        'M100,20 C144,20 180,56 180,100 C180,144 144,180 100,180 C56,180 20,144 20,100 C20,56 56,20 100,20 Z',
        'M100,24 C150,12 186,60 178,104 C170,148 138,184 96,178 C54,172 18,140 24,96 C30,52 50,36 100,24 Z',
        'M100,18 C160,18 182,40 182,100 C182,160 160,182 100,182 C40,182 18,160 18,100 C18,40 40,18 100,18 Z',
        'M104,22 C148,30 178,64 174,108 C170,152 134,182 92,176 C50,170 22,132 26,90 C30,48 60,14 104,22 Z',
        'M100,26 C132,10 172,44 176,92 C180,140 152,180 108,180 C64,180 16,156 22,108 C28,60 68,42 100,26 Z',
        'M100,20 C140,36 180,60 176,104 C172,148 140,178 98,180 C56,182 24,150 22,106 C20,62 60,4 100,20 Z'
      ];

      var orb = document.createElement('div');
      orb.className = 'ryzo-orb';
      orb.setAttribute('aria-hidden', 'true');
      orb.innerHTML =
        '<div class="ryzo-orb__float">' +
          '<div class="ryzo-orb__spin">' +
            '<svg class="ryzo-orb__body" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">' +
              '<defs>' +
                '<radialGradient id="ryzoOrbG" cx="32%" cy="28%" r="80%">' +
                  '<stop offset="0%" stop-color="#f0abfc"/>' +
                  '<stop offset="42%" stop-color="#b121b7"/>' +
                  '<stop offset="100%" stop-color="#4c1d95"/>' +
                '</radialGradient>' +
                '<filter id="ryzoOrbBlur" x="-40%" y="-40%" width="180%" height="180%">' +
                  '<feGaussianBlur stdDeviation="8"/>' +
                '</filter>' +
              '</defs>' +
              '<path class="ryzo-orb__blob" d="' + BLOBS[0] + '" fill="url(#ryzoOrbG)"/>' +
              '<ellipse cx="72" cy="56" rx="32" ry="20" fill="rgba(255,255,255,0.55)" filter="url(#ryzoOrbBlur)"/>' +
            '</svg>' +
          '</div>' +
          '<svg class="ryzo-orb__icon" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>' +
          '</svg>' +
        '</div>';
      document.body.appendChild(orb);

      var blobEl = orb.querySelector('.ryzo-orb__blob');
      var spinEl = orb.querySelector('.ryzo-orb__spin');
      var iconEl = orb.querySelector('.ryzo-orb__icon');
      var floatEl = orb.querySelector('.ryzo-orb__float');
      var hasMorph = !!window.MorphSVGPlugin;
      if (hasMorph) gsap.registerPlugin(window.MorphSVGPlugin);
      gsap.set(spinEl, { transformPerspective: 520 });

      /* Idle life — the orb breathes and drifts on its own, independent of
         scroll. Different sine periods per axis give an organic, living
         wander instead of a mechanical loop. */
      gsap.to(floatEl, { y: 9, duration: 2.7, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to(floatEl, { x: 6, duration: 3.9, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to(floatEl, { scale: 1.055, duration: 2.1, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to(blobEl, { rotation: 6, duration: 3.2, yoyo: true, repeat: -1, ease: 'sine.inOut', transformOrigin: '50% 50%' });
      gsap.to(iconEl, { y: 4, duration: 1.7, yoyo: true, repeat: -1, ease: 'sine.inOut' });

      var orbStop = function (i) {
        var vw = window.innerWidth;
        var vh = window.innerHeight;
        var last = slides.length - 1;
        if (i === 0) return { x: vw * 0.36, y: vh * 0.26, scale: 1, opacity: 1 };
        if (i === last) return { x: 0, y: -vh * 0.04, scale: 3.4, opacity: 0.14 };
        var side = i % 2 === 1 ? -1 : 1;
        var lift = [-0.2, 0.04, 0.22][i % 3];
        return {
          x: side * (vw / 2 - Math.max(100, vw * 0.08)),
          y: vh * lift,
          scale: 0.66 + 0.4 * (((i * 7) % 5) / 4),
          opacity: 1
        };
      };

      gsap.set(orb, Object.assign({ xPercent: -50, yPercent: -50 }, orbStop(0)));

      var orbTl = gsap.timeline({
        scrollTrigger: {
          trigger: document.body,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.6,
          invalidateOnRefresh: true
        }
      });
      slides.forEach(function (slide, i) {
        if (i === 0) return;
        var at = i - 1;
        orbTl.to(orb, {
          x: function () { return orbStop(i).x; },
          y: function () { return orbStop(i).y; },
          scale: function () { return orbStop(i).scale; },
          opacity: function () { return orbStop(i).opacity; },
          duration: 1,
          ease: 'power1.inOut'
        }, at);
        /* Full 3D flip between stops — lands face-on every time. */
        orbTl.to(spinEl, {
          rotationY: '+=360',
          duration: 1,
          ease: 'power1.inOut'
        }, at);
        /* Evolve: morph into the next blob shape while travelling. */
        if (hasMorph) {
          orbTl.to(blobEl, {
            morphSVG: BLOBS[i % BLOBS.length],
            duration: 1,
            ease: 'power1.inOut'
          }, at);
        }
        /* Slow hue drift across the whole story. */
        orbTl.to(orb, {
          filter: 'hue-rotate(' + i * 7 + 'deg)',
          duration: 1,
          ease: 'none'
        }, at);
      });
    }

    /* ----- "Try it" comet: the button's energy flies to the real widget
       and lands exactly where the demo lives, with a landing pulse. ----- */
    var FLIGHT_TARGETS = {
      'float-image-search': ['#float-image-icon', '.ryzo-image-search-container'],
      'float-text-search': ['.ryzo-text-search-container', '#float-image-icon'],
      'text-search': ['.ryzo-search-bar-container', '.header__search'],
      'product-recommendation': []
    };

    function visibleTarget(selectors) {
      for (var s = 0; s < selectors.length; s++) {
        var el = document.querySelector(selectors[s]);
        if (!el) continue;
        var r = el.getBoundingClientRect();
        if (r.width > 4 && r.height > 4) return el;
      }
      return null;
    }

    function pulseAt(cx, cy) {
      for (var k = 0; k < 3; k++) {
        (function (k) {
          var ring = document.createElement('div');
          ring.className = 'ryzo-comet-ring';
          document.body.appendChild(ring);
          gsap.set(ring, { x: cx - 13, y: cy - 13, scale: 0.6, opacity: 0.9 });
          gsap.to(ring, {
            scale: 2.4 + k,
            opacity: 0,
            duration: 0.9,
            delay: k * 0.16,
            ease: 'power2.out',
            onComplete: function () { ring.remove(); }
          });
        })(k);
      }
    }

    function spotlight(targetEl) {
      var tr = targetEl.getBoundingClientRect();
      pulseAt(tr.left + tr.width / 2, tr.top + tr.height / 2);
      targetEl.classList.remove('ryzo-target-spotlight');
      void targetEl.offsetWidth;
      targetEl.classList.add('ryzo-target-spotlight');
      setTimeout(function () { targetEl.classList.remove('ryzo-target-spotlight'); }, 1800);
    }

    /* The button itself lifts off, flies to the widget, points at it with
       a couple of nudges, then dissolves while the original fades back. */
    function launchTryFlight(btn, targetEl) {
      if (btn.__ryzoFlying) return;
      btn.__ryzoFlying = true;

      var r = btn.getBoundingClientRect();
      var clone = btn.cloneNode(true);
      clone.classList.add('ryzo-try-flight');
      clone.style.width = r.width + 'px';
      clone.style.height = r.height + 'px';
      document.body.appendChild(clone);
      gsap.set(clone, { x: r.left, y: r.top });
      gsap.set(btn, { opacity: 0 });

      var tl = gsap.timeline({
        onComplete: function () {
          clone.remove();
          /* Bring the original back softly — no pop. */
          gsap.to(btn, { opacity: 1, duration: 0.9, delay: 0.25, ease: 'power1.inOut' });
          btn.__ryzoFlying = false;
        }
      });

      if (targetEl) {
        var t = targetEl.getBoundingClientRect();
        /* Land beside the widget — above it, or below when it sits high. */
        var below = t.top < r.height + 90;
        var lx = Math.min(Math.max(t.left + t.width / 2 - r.width / 2, 12), window.innerWidth - r.width - 12);
        var ly = below ? t.bottom + 16 : t.top - r.height - 16;
        var pokeY = below ? -10 : 10;

        /* One direct, smooth glide — no detours. */
        tl.to(clone, { x: lx, y: ly, scale: 1.04, duration: 0.7, ease: 'power2.inOut' })
          .add(function () { spotlight(targetEl); })
          /* Point at it: nudge toward the widget and back, twice. */
          .to(clone, { y: ly + pokeY, duration: 0.18, ease: 'sine.inOut', yoyo: true, repeat: 3 })
          /* Dissolve right where it landed. */
          .to(clone, { opacity: 0, scale: 0.9, duration: 0.4, ease: 'power2.out', delay: 0.35 });
      } else {
        /* No visible widget — pulse in place instead. */
        tl.to(clone, { scale: 1.08, duration: 0.22, ease: 'power2.out' })
          .add(function () { pulseAt(r.left + r.width / 2, r.top + r.height / 2); })
          .to(clone, { scale: 1, duration: 0.25, ease: 'power2.inOut' })
          .to(clone, { opacity: 0, duration: 0.3, delay: 0.25 });
      }
    }

    document.querySelectorAll('.ryzo-shot__try[data-app]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sels = FLIGHT_TARGETS[btn.getAttribute('data-app')] || [];
        launchTryFlight(btn, visibleTarget(sels));
      });
    });

    /* ----- Demo scene: image search -----
       A looping product story: the cursor picks the All Star photo in the
       "Photos" window, drags it into the Ryzo modal, a scan sweeps it, and
       the matching results pop in. Built lazily on first arrival (layout
       settled), plays only while its chapter is on screen. */
    document.querySelectorAll('[data-ryzo-scene="image-search"]').forEach(function (scene) {
      if (window.matchMedia('(max-width: 720px)').matches) return; // static on phones

      var target = scene.querySelector('.ryzo-scene__thumb--target');
      var hint = scene.querySelector('.ryzo-scene__drop-hint');
      var uploaded = scene.querySelector('.ryzo-scene__uploaded');
      var scan = scene.querySelector('.ryzo-scene__scan');
      var drop = scene.querySelector('.ryzo-scene__drop');
      var cards = scene.querySelectorAll('.ryzo-scene__card');
      var ghost = scene.querySelector('.ryzo-scene__ghost');
      var cursor = scene.querySelector('.ryzo-scene__cursor');
      if (!target || !uploaded || !drop || !ghost || !cursor) return;

      var tl = null;

      var center = function (el) {
        var r = el.getBoundingClientRect();
        var s = scene.getBoundingClientRect();
        return { x: r.left - s.left + r.width / 2, y: r.top - s.top + r.height / 2 };
      };

      var build = function () {
        var tPos = center(target);
        var dPos = center(drop);
        var startPos = center(scene.querySelector('.ryzo-scene__modal-head'));

        tl = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 2.2 });

        /* rewind */
        tl.set(hint, { opacity: 1 })
          .set(uploaded, { opacity: 0, scale: 0.85 })
          .set(scan, { opacity: 0, top: '12%' })
          .set(cards, { opacity: 0, y: 16 })
          .set(ghost, { opacity: 0 })
          .set(cursor, { x: startPos.x, y: startPos.y, scale: 1, opacity: 0 })
          .call(function () { target.classList.remove('is-selected'); })

          /* act 1 · pick the photo */
          .to(cursor, { opacity: 1, duration: 0.25 })
          .to(cursor, { x: tPos.x + 5, y: tPos.y + 7, duration: 0.9, ease: 'power2.inOut' })
          .to(cursor, { scale: 0.82, duration: 0.09, yoyo: true, repeat: 1 })
          .call(function () { target.classList.add('is-selected'); })

          /* act 2 · drag it into the modal */
          .set(ghost, { x: tPos.x - 44, y: tPos.y - 44, scale: 1 })
          .to(ghost, { opacity: 1, duration: 0.18 })
          .to(ghost, { x: dPos.x - 44, y: dPos.y - 44, scale: 0.8, duration: 0.9, ease: 'power2.inOut' }, '<')
          .to(cursor, { x: dPos.x + 6, y: dPos.y + 8, duration: 0.9, ease: 'power2.inOut' }, '<')
          .to(ghost, { opacity: 0, scale: 0.55, duration: 0.25 })
          .to(hint, { opacity: 0, duration: 0.2 }, '<')
          .to(cursor, { opacity: 0, duration: 0.35 }, '<')
          .fromTo(uploaded, { opacity: 0, scale: 0.85 }, { opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(1.6)' })

          /* act 3 · AI scan */
          .to(scan, { opacity: 1, duration: 0.15 })
          .to(scan, { top: '85%', duration: 0.65, ease: 'power1.inOut', yoyo: true, repeat: 1 })
          .to(scan, { opacity: 0, duration: 0.2 })

          /* act 4 · the results arrive */
          .to(cards, { opacity: 1, y: 0, duration: 0.5, stagger: 0.07, ease: 'back.out(1.7)' })
          .call(function () { target.classList.remove('is-selected'); }, null, '+=1.4');
      };

      ScrollTrigger.create({
        trigger: scene,
        start: 'top 75%',
        end: 'bottom 15%',
        onToggle: function (self) {
          if (self.isActive) {
            if (!tl) build();
            tl.play();
          } else if (tl) {
            tl.pause();
          }
        }
      });
    });

    /* ----- Demo scene: AI text search -----
       A conversational query types itself, Ryzo exposes the intent it found
       in the sentence, then the matching linen dresses arrive. The HTML and
       CSS hold the completed frame by default; this loop only rewinds it for
       motion-capable desktop visitors. */
    document.querySelectorAll('[data-ryzo-scene="ai-search"]').forEach(function (scene) {
      if (window.matchMedia('(max-width: 720px)').matches) return; // static on phones

      var search = scene.querySelector('.ryzo-ai-scene__search');
      var queryEl = scene.querySelector('.ryzo-ai-scene__query');
      var caret = scene.querySelector('.ryzo-ai-scene__caret');
      var enter = scene.querySelector('.ryzo-ai-scene__enter');
      var suggestion = scene.querySelector('.ryzo-ai-scene__suggestion');
      var thinking = scene.querySelector('.ryzo-ai-scene__thinking');
      var spinner = scene.querySelector('.ryzo-ai-scene__spinner');
      var intent = scene.querySelector('.ryzo-ai-scene__intent');
      var intentPieces = scene.querySelectorAll('.ryzo-ai-scene__intent > span');
      var resultHead = scene.querySelector('.ryzo-ai-scene__result-head');
      var cards = scene.querySelectorAll('.ryzo-ai-scene__card');
      if (!search || !queryEl || !suggestion || !thinking || !intent || !cards.length) return;

      var query = 'white linen dress for the beach';
      var typeState = { count: query.length };
      var tl = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 2.4 });

      /* rewind */
      tl.set(suggestion, { opacity: 0, y: -5 })
        .set(thinking, { opacity: 0, y: 5 })
        .set(spinner, { rotation: 0 })
        .set(intent, { opacity: 0 })
        .set(intentPieces, { opacity: 0, y: 8, scale: 0.96 })
        .set(resultHead, { opacity: 0, y: 7 })
        .set(cards, { opacity: 0, y: 18, scale: 0.96 })
        .set(caret, { opacity: 1 })
        .set(enter, { scale: 1, y: 0 })
        .call(function () {
          typeState.count = 0;
          queryEl.textContent = '';
          search.classList.add('is-focused');
        })

        /* act 1 · type the way a shopper really speaks */
        .fromTo(search, { scale: 0.985 }, { scale: 1, duration: 0.3, ease: 'power2.out' })
        .to(typeState, {
          count: query.length,
          duration: 2.25,
          ease: 'none',
          snap: { count: 1 },
          onUpdate: function () {
            queryEl.textContent = query.slice(0, Math.round(typeState.count));
          }
        })
        .to(suggestion, { opacity: 1, y: 0, duration: 0.32, ease: 'power2.out' }, 0.85)
        .to(enter, { scale: 0.82, y: 2, duration: 0.1, yoyo: true, repeat: 1, ease: 'power1.inOut' })
        .to(suggestion, { opacity: 0, y: -5, duration: 0.24 }, '<0.1')
        .to(caret, { opacity: 0, duration: 0.16 }, '<')

        /* act 2 · make the language understanding visible */
        .to(thinking, { opacity: 1, y: 0, duration: 0.28 })
        .to(spinner, { rotation: 720, duration: 1.05, ease: 'none' }, '<')
        .to(thinking, { opacity: 0, y: -5, duration: 0.22 }, '-=0.12')
        .set(intent, { opacity: 1 })
        .to(intentPieces, { opacity: 1, y: 0, scale: 1, duration: 0.38, stagger: 0.09, ease: 'back.out(1.8)' })

        /* act 3 · best matches, not keyword noise */
        .to(resultHead, { opacity: 1, y: 0, duration: 0.3 }, '-=0.1')
        .to(cards, { opacity: 1, y: 0, scale: 1, duration: 0.48, stagger: 0.065, ease: 'back.out(1.55)' }, '-=0.08')
        .call(function () { search.classList.remove('is-focused'); }, null, '+=1.5');

      ScrollTrigger.create({
        trigger: scene,
        start: 'top 75%',
        end: 'bottom 15%',
        onToggle: function (self) {
          if (self.isActive) tl.play();
          else tl.pause();
        }
      });
    });

    /* ----- Story chapters: header text staggers in on every visit -----
       The screenshot reveal lives on .ryzo-shot__image (CSS `is-visible`);
       GSAP animates the narrative header so each chapter "tells" itself. */
    document.querySelectorAll('.ryzo-shot--chapter .ryzo-shot__meta').forEach(function (meta) {
      var pieces = meta.children;
      if (!pieces.length) return;
      gsap.from(pieces, {
        opacity: 0,
        y: 26,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.09,
        scrollTrigger: {
          trigger: meta.closest('.ryzo-shot'),
          start: 'top 62%',
          toggleActions: 'play none none reverse'
        }
      });
    });

    /* ----- Screenshot slides: parallax drift on the frame ----- */
    if (window.matchMedia('(min-width: 721px)').matches) {
      document.querySelectorAll('.ryzo-shot').forEach(function (shot) {
        var frame = shot.querySelector('.ryzo-shot__frame');
        if (!frame) return;
        gsap.fromTo(
          frame,
          { yPercent: 4.5 },
          {
            yPercent: -4.5,
            ease: 'none',
            scrollTrigger: {
              trigger: shot,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.4
            }
          }
        );
      });
    }

    /* ----- "See the story" → scroll to the first chapter ----- */
    document.querySelectorAll('[data-ryzo-scroll-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var page = btn.closest('.ryzo-snap-page');
        var i = slides.indexOf(page);
        var next = slides[i + 1];
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    /* ----- CTA slide entrance ----- */
    var cta = document.querySelector('.ryzo-cta');
    if (cta) {
      var pieces = cta.querySelectorAll('[data-ryzo-cta-stagger]');
      if (pieces.length) {
        gsap.from(pieces, {
          opacity: 0,
          y: 28,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.09,
          scrollTrigger: {
            trigger: cta,
            start: 'top 65%',
            toggleActions: 'play none none reverse'
          }
        });
      }
    }

    /* ----- "Chat with us" → open Crisp if present ----- */
    document.querySelectorAll('[data-ryzo-open-chat]').forEach(function (btn) {
      if (!window.$crisp) {
        btn.hidden = true;
        return;
      }
      btn.addEventListener('click', function () {
        window.$crisp.push(['do', 'chat:open']);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
